/* @jsx h */
import { defineComponent, h, onBeforeUnmount, onMounted, Teleport } from "vue";

export type LightboxProps = {
  src: string;
  alt?: string;
  /** Accessible label for the close button; pass a translated string. */
  closeLabel?: string;
  onClose: () => void;
};

// Full-screen, uncropped view of a single image. Closes on backdrop click,
// the close button, or Escape.
export const Lightbox = defineComponent<LightboxProps>({
  name: "Lightbox",
  props: ["src", "alt", "closeLabel", "onClose"],
  setup(props) {
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };

    let previousOverflow = "";
    onMounted(() => {
      document.addEventListener("keydown", onKeydown);
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    });
    onBeforeUnmount(() => {
      document.removeEventListener("keydown", onKeydown);
      document.body.style.overflow = previousOverflow;
    });

    return () => (
      <Teleport to="body">
        <div
          class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90 p-4"
          style={{
            paddingTop: "calc(var(--safe-area-top, 0px) + 1rem)",
            paddingBottom:
              "calc(var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 1rem)",
          }}
          role="dialog"
          aria-modal="true"
          aria-label={props.alt}
          onClick={() => props.onClose()}
        >
          <button
            type="button"
            class="btn btn-circle btn-ghost absolute right-4 text-white hover:bg-white/10"
            style={{ top: "calc(var(--safe-area-top, 0px) + 1rem)" }}
            aria-label={props.closeLabel ?? "Close"}
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              props.onClose();
            }}
          >
            <span class="material-symbols-outlined" aria-hidden="true">
              close
            </span>
          </button>
          <img
            src={props.src}
            alt={props.alt}
            class="max-h-full max-w-full object-contain"
            onClick={(e: MouseEvent) => e.stopPropagation()}
          />
        </div>
      </Teleport>
    );
  },
});
